import type {
  Aircraft,
  AirportConfigSet,
  AirportConfigUpdate,
  EventEffect,
  EventScopeType,
  FlightCancellation,
  FlightOccurrence,
  FlightOccurrenceEvent,
  FlightPlan,
  FlightReleaseDraft,
  GlobalEvent,
  OpsDayCloseResult,
  PassengerTransfer,
  CustomFlightKind,
  RedispatchAction,
  SaveGame,
  ScheduleSource,
  ServiceDay,
  WorldData
} from "../domain/types";
import type { FlightLedgerPreviewOverrides, FlightLedgerResult } from "../domain/simulation";


export interface PublicUser {
  id: string;
  email: string;
}

export interface GameStateResponse {
  user?: PublicUser;
  admin?: boolean;
  save: SaveGame | null;
  world: WorldData;
  airportConfigs: AirportConfigSet;
  activeEvents: GlobalEvent[];
  schedule: ScheduleSource;
}

export interface AdminAuditLogRecord {
  id: string;
  actorUserId: string;
  action: string;
  targetType: string;
  targetId: string;
  payload: unknown;
  createdAt: string;
}

export async function startAuth(email: string) {
  return request<{ ok: boolean; email: string; devCode?: string; expiresAt: string; message: string }>("/api/auth/start", {
    method: "POST",
    body: { email }
  });
}

export async function verifyAuth(email: string, code: string) {
  return request<{ ok: boolean; user: PublicUser; admin: boolean }>("/api/auth/verify", {
    method: "POST",
    body: { email, code }
  });
}

export async function logout() {
  return request<{ ok: boolean }>("/api/auth/logout", { method: "POST" });
}

export async function fetchGameState() {
  return request<GameStateResponse>("/api/game/state");
}

export async function createNewGame(airlineName: string) {
  return request<{ save: SaveGame; world: WorldData; airportConfigs: AirportConfigSet; schedule: ScheduleSource }>("/api/game/new", {
    method: "POST",
    body: { airlineName }
  });
}

export async function createCustomFlight(body: {
  kind: CustomFlightKind;
  title?: string;
  origin: string;
  destination: string;
  scheduledDay: number;
  departureTimeLocal: string;
  aircraftTypeId: string;
  recoveryForFlightInstanceId?: string;
  passengerTransfers?: PassengerTransfer[];
  linkedProgramId?: string;
  linkedLegSequence?: number;
}) {
  return request<{ save: SaveGame }>("/api/game/custom-flights", { method: "POST", body });
}

export async function createPlayerRotation(body: { name?: string; occurrenceIds?: string[]; scheduledFlightIds?: string[] }) {
  return request<{ save: SaveGame }>("/api/game/rotations", { method: "POST", body });
}

export async function updatePlayerRotation(id: string, body: { name?: string; occurrenceIds?: string[]; scheduledFlightIds?: string[] }) {
  return request<{ save: SaveGame }>(`/api/game/rotations/${id}`, { method: "PUT", body });
}

export async function deletePlayerRotation(id: string) {
  return request<{ save: SaveGame }>(`/api/game/rotations/${id}`, { method: "DELETE" });
}

export async function createAdminAircraft(body: Omit<Aircraft, "id">) {
  return request<{ save: SaveGame }>("/api/admin/aircraft", { method: "POST", body });
}

export async function updateAdminAircraft(id: string, body: Omit<Aircraft, "id">) {
  return request<{ save: SaveGame }>(`/api/admin/aircraft/${id}`, { method: "PUT", body });
}

export async function deleteAdminAircraft(id: string) {
  return request<{ save: SaveGame }>(`/api/admin/aircraft/${id}`, { method: "DELETE" });
}

export async function fetchAdminAirportConfigs() {
  return request<{ airportConfigs: AirportConfigSet }>("/api/admin/airports/configs");
}

export async function updateAdminAirportConfig(input: AirportConfigUpdate) {
  return request<{ airportConfigs: AirportConfigSet }>(`/api/admin/airports/configs/${input.airportCode}`, {
    method: "PUT",
    body: input
  });
}

export async function startOpsDay() {
  return request<{ save: SaveGame; world: WorldData; schedule: ScheduleSource; activeEvents: GlobalEvent[] }>("/api/game/ops/start-day", { method: "POST" });
}

export async function tickOpsDay(minutes: number) {
  return request<{ save: SaveGame; world: WorldData; schedule: ScheduleSource; activeEvents: GlobalEvent[] }>("/api/game/ops/tick", { method: "POST", body: { minutes } });
}

export async function redispatchFlight(body: RedispatchAction) {
  return request<{ save: SaveGame; world: WorldData; schedule: ScheduleSource; activeEvents: GlobalEvent[] }>("/api/game/ops/redispatch", { method: "POST", body });
}

export async function fetchFlightReleaseDraft(flightInstanceId: string) {
  return request<{ releaseDraft: FlightReleaseDraft }>(`/api/game/ops/flights/${flightInstanceId}/release-draft`);
}

export async function fetchFlightLedgerPreview(
  flightInstanceId: string,
  overrides: FlightLedgerPreviewOverrides = {}
) {
  return request<{ ledger: FlightLedgerResult | undefined }>(`/api/game/ops/flights/${flightInstanceId}/ledger-preview`, { method: "POST", body: overrides });
}

export async function closeOpsDay() {
  return request<OpsDayCloseResult & { world: WorldData; schedule: ScheduleSource }>("/api/game/ops/close-day", { method: "POST" });
}

export async function fetchSchedule() {
  return request<{ schedule: ScheduleSource }>("/api/world/schedule");
}

export async function fetchActiveEvents() {
  return request<{ events: GlobalEvent[]; generatedAt: string }>("/api/world/events/active");
}

export async function fetchAdminEvents() {
  return request<{ events: GlobalEvent[] }>("/api/admin/events");
}

export async function fetchAdminFlightPlans() {
  return request<{ schedule: ScheduleSource }>("/api/admin/flight-plans");
}

export async function createAdminEvent(body: {
  title: string;
  severity: GlobalEvent["severity"];
  scopeType: EventScopeType;
  scopeValue: string;
  reason: string;
  effects: EventEffect[];
  activateNow?: boolean;
  endsAt?: string;
}) {
  return request<{ event: GlobalEvent }>("/api/admin/events", { method: "POST", body });
}

export async function activateAdminEvent(id: string) {
  return request<{ event: GlobalEvent }>(`/api/admin/events/${id}/activate`, { method: "POST" });
}

export async function cancelAdminEvent(id: string) {
  return request<{ event: GlobalEvent }>(`/api/admin/events/${id}/cancel`, { method: "POST" });
}

export async function createAdminFlightPlan(body: {
  flightNo: string;
  origin: string;
  destination: string;
  serviceDays: ServiceDay[];
  startsDay: number;
  endsDay?: number;
  departureTimeLocal: string;
  minimumTurnMinutes: number;
  priority: number;
}) {
  return request<{ flightPlan: FlightPlan; flightPlans: FlightPlan[]; occurrences: FlightOccurrence[]; schedule: ScheduleSource }>("/api/admin/flight-plans", { method: "POST", body });
}

export async function cancelAdminFlightPlan(id: string, reason: string) {
  return request<{ flightPlan: FlightPlan; schedule: ScheduleSource }>(`/api/admin/flight-plans/${id}/cancel`, { method: "POST", body: { reason } });
}

export async function createAdminFlightCancellation(body: {
  occurrenceId: string;
  day: number;
  dateIso: string;
  reason: string;
}) {
  return request<{ cancellation: FlightCancellation; schedule: ScheduleSource }>("/api/admin/flight-cancellations", { method: "POST", body });
}

export async function createAdminFlightOccurrenceEvent(body: Omit<FlightOccurrenceEvent, "id" | "status" | "createdBy" | "createdAt">) {
  return request<{ event: FlightOccurrenceEvent; schedule: ScheduleSource }>("/api/admin/flight-occurrence-events", { method: "POST", body });
}

export async function fetchAdminAuditLog() {
  return request<{ auditLog: AdminAuditLogRecord[] }>("/api/admin/audit-log");
}

async function request<T>(path: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
  const response = await fetch(path, {
    method: options.method ?? "GET",
    credentials: "include",
    headers: requestHeaders(options.body),
    body: requestBody(options.body)
  });
  const payload = (await response.json().catch(() => null)) as T | { error?: string } | null;
  if (!response.ok) {
    const error = responseErrorMessage(payload);
    throw new Error(error || `HTTP ${response.status}`);
  }
  return payload as T;
}

function requestHeaders(body: unknown): HeadersInit | undefined {
  if (body) {
    return { "Content-Type": "application/json" };
  }
  return undefined;
}

function requestBody(body: unknown): BodyInit | undefined {
  if (body) {
    return JSON.stringify(body);
  }
  return undefined;
}

function responseErrorMessage(payload: unknown): string | undefined {
  if (typeof payload !== "object" || payload === null || !("error" in payload)) {
    return undefined;
  }
  const error = (payload as { error?: unknown }).error;
  if (typeof error === "string") {
    return error;
  }
  return undefined;
}
